<template>
  <div
    v-if="showCheckbox"
    class="col-12"
  >
    <hr-checkbox
      :model-value="modelValue"
      :disable="isDisabled"
      :label="t('scheduling.notifyStaff.checkboxTitle')"
      data-e2e="checkbox-notify-staff"
      @update:model-value="$emit('update:modelValue', $event)"
    />
    <hr-p
      v-if="caption"
      :text="caption"
      class="notify-staff__caption hr-body-small hr-text-on-surface-variant"
      data-e2e="caption-notify-staff"
    />
  </div>
</template>

<script setup>
import { HrCheckbox, HrP } from 'src/design-system';
import {
  NOTIFY_STAFF_AVAILABILITY,
  isNotifyStaffDisabled,
  isNotifyStaffShown,
} from 'src/utils/NotificationAvailability';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * See src/utils/NotificationAvailability.js for the values. 'none' also covers callers that
   * cannot notify staff at all, so no separate opt-in prop is needed.
   */
  availability: {
    type: String,
    default: 'none',
    validator: (value) => NOTIFY_STAFF_AVAILABILITY.includes(value),
  },
  /**
   * Caption overrides per availability value. Only `disabledSelf` and `disabledUnsaved` need one:
   * their wording names what the reader just did, which this shared component cannot know. The
   * settings-driven `disabled` and `partial` captions are the same everywhere and come from here.
   */
  captions: {
    type: Object,
    default: () => ({}),
  },
});

defineEmits(['update:modelValue']);

const { t } = useI18n();

const showCheckbox = computed(() => isNotifyStaffShown(props.availability));
const isDisabled = computed(() => isNotifyStaffDisabled(props.availability));
const caption = computed(() => ({
  disabled: t('scheduling.notifyStaff.disabledCaption'),
  partial: t('scheduling.notifyStaff.partialCaption'),
  ...props.captions,
}[props.availability] ?? null));
</script>

<style lang="scss" scoped>
.notify-staff__caption {
  padding-inline-start: 10px;
}
</style>
